import { useEditor, EditorContent } from "@tiptap/react";
import Paragraph from "@tiptap/extension-paragraph";
import { mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { Image } from "@tiptap/extension-image";
import { TextStyle, FontSize } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import { FontFamily } from "@tiptap/extension-font-family";
import { Link } from "@tiptap/extension-link";

// Extend TipTap's Link mark and RENDER IT AS A <span>, not <a>.
// This is the only bulletproof way to stop Chrome from opening a new tab
// when clicking a link inside a contentEditable region — because there is
// no anchor navigation target for the browser to follow. The actual jump
// target (a note anchor OR an attached-doc page) is stored in `data-eo-*`
// attributes; FileEditor's capture-phase click listener reads them and
// either scrolls to the note or opens the split view at the requested page.
const EDocsLink = Link.extend({
  name: "link",
  inclusive: false,
  addAttributes() {
    const parent = this.parent?.() || {};
    const dataAttr = (key) => ({
      default: null,
      parseHTML: (el) => el.getAttribute(key),
      renderHTML: (attrs) => (attrs[key] ? { [key]: attrs[key] } : {}),
    });
    return {
      ...parent,
      // Drop the anchor-only `target` attribute entirely.
      target: { default: null, parseHTML: () => null, renderHTML: () => ({}) },
      "data-eo-note": dataAttr("data-eo-note"),
      "data-eo-doc": dataAttr("data-eo-doc"),
      "data-eo-page": dataAttr("data-eo-page"),
      "data-eo-draft": dataAttr("data-eo-draft"),
    };
  },
  // Accept BOTH the new `<span data-eo-*>` format AND legacy `<a href="#note-..">`
  // / `<a href="#doc:...">` shapes so old files keep working.
  parseHTML() {
    return [
      { tag: "span[data-eo-note]" },
      { tag: "span[data-eo-doc]" },
      { tag: "span[data-eo-draft]" },
      { tag: 'a[href^="#note-"]' },
      { tag: 'a[href^="#doc:"]' },
      { tag: 'a[href^="#draft:"]' },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    // Strip href so the DOM never emits a navigable anchor. mergeAttributes
    // orders options → per-mark attrs, so we drop href in the second object.
    const { href: _drop, ...rest } = HTMLAttributes;
    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, rest, { role: "link", tabindex: "0" }),
      0,
    ];
  },
});

// Override the paragraph node so pressing Enter always splits with keepMarks:true.
// Without this, inline marks like TextStyle/FontFamily/FontSize/Color/Highlight
// silently drop on the new paragraph, forcing the author to re-pick the font
// every time.
const KeepMarksParagraph = Paragraph.extend({
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => editor.commands.splitBlock({ keepMarks: true }),
    };
  },
});

import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading1, Heading2,
  Quote, Undo2, Redo2, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Table as TableIcon, Image as ImageIcon, Palette, Highlighter,
  Plus, Minus, Trash2, Link as LinkIcon, X as XIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const SIZES = ["10px", "12px", "14px", "16px", "18px", "20px", "24px", "28px", "32px", "40px"];
const FONTS = [
  { label: "Serif", value: "'Playfair Display', Georgia, serif" },
  { label: "Sans", value: "'IBM Plex Sans', sans-serif" },
  { label: "Mono", value: "'IBM Plex Mono', monospace" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Arial", value: "Arial, sans-serif" },
  { label: "Courier", value: "'Courier New', monospace" },
];
const COLORS = ["#0A0A0A", "#1D3557", "#E63946", "#2A9D8F", "#F4A261", "#5C5C54", "#8A8A80", "#B8B5AA", "#457B9D"];
const HIGHLIGHTS = ["#FFF59D", "#B2EBF2", "#F8BBD0", "#DCEDC8", "#FFE0B2", "#E1BEE7", null];

// Sanitize HTML from an external paste so pasted text obeys the editor's own
// font-family / size / color rules. Browsers embed inline styles like
// `style="font-family: Times; font-size: 14px"` on every span/paragraph they
// produce; if we keep them the user's toolbar "select-all → change font" only
// updates TipTap marks, and the inline styles win visually. Also drops MS Word
// class junk (`Mso*`), most classes, and any unknown attributes on <font>/<b>.
const FORMATTING_STYLE_KEYS = new Set([
  "font-family", "font-size", "font-weight", "font-style", "font",
  "color", "background", "background-color",
  "line-height", "letter-spacing", "text-transform", "text-decoration",
  "mso-ansi-language", "mso-bidi-font-family", "mso-fareast-font-family",
  "mso-bidi-language", "mso-fareast-language",
]);
function sanitizePastedHtml(html) {
  if (typeof window === "undefined" || !html) return html;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    // Remove MS Office namespaced junk (<o:p>, <w:*>, <m:*>, <v:*>). CSS
    // selectors don't allow wildcard prefixes, so scan all elements once.
    doc.querySelectorAll("*").forEach((el) => {
      const tag = (el.tagName || "").toLowerCase();
      if (/^(o|w|m|v):/.test(tag)) {
        el.remove();
        return;
      }
      // Kill inline base64 images. A single pasted screenshot can weigh
      // 2-5 MB of base64 — that HTML then ships to the backend on every
      // autosave, tanking editor performance. Users are expected to attach
      // real images via the Depository so they live out-of-band.
      if (tag === "img") {
        const src = el.getAttribute && el.getAttribute("src");
        if (src && /^data:/i.test(src)) {
          el.replaceWith(doc.createTextNode("[image removed — please attach it via the Depository]"));
          return;
        }
      }
      // Strip disallowed inline styles (font-family, size, color, etc.)
      const style = el.getAttribute && el.getAttribute("style");
      if (style) {
        const kept = style
          .split(";")
          .map((s) => s.trim())
          .filter(Boolean)
          .filter((decl) => {
            const key = decl.split(":", 1)[0].trim().toLowerCase();
            return !FORMATTING_STYLE_KEYS.has(key);
          });
        if (kept.length) el.setAttribute("style", kept.join("; "));
        else el.removeAttribute("style");
      }
      // Strip class attributes (kills Word's `MsoNormal` etc.)
      if (el.hasAttribute && el.hasAttribute("class")) el.removeAttribute("class");
      // Legacy <font face="..." size="..." color="..."> attributes
      if (tag === "font") {
        ["face", "size", "color"].forEach((a) => el.removeAttribute(a));
      }
    });
    return doc.body ? doc.body.innerHTML : html;
  } catch (e) {
    // Fail open — never block a paste because the sanitizer errored.
    // eslint-disable-next-line no-console
    console.warn("sanitizePastedHtml failed:", e);
    return html;
  }
}

export default function RichEditor({
  value, onChange, editable = true, placeholder = "Start writing...",
  noteCount = 0, attachedDocs = [], fileDrafts = [], onOpenDraft, toolbarTarget = null,
}) {
  const placeToolbar = (node) => (toolbarTarget ? createPortal(node, toolbarTarget) : node);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ paragraph: false }),
      KeepMarksParagraph,
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      FontFamily,
      FontSize,
      EDocsLink.configure({
        openOnClick: false,
        autolink: false,
        linkOnPaste: false,
        HTMLAttributes: {
          target: null,
          rel: "noopener noreferrer",
          class: "eo-note-link",
        },
        isAllowedUri: () => true,
      }),
      Image.configure({ inline: false, allowBase64: true, HTMLAttributes: { class: "eo-editor-img" } }),
      Table.configure({ resizable: true, HTMLAttributes: { class: "eo-editor-table" } }),
      TableRow, TableHeader, TableCell,
    ],
    content: value || "",
    editable,
    editorProps: {
      transformPastedHTML: (html) => sanitizePastedHtml(html),
    },
    onUpdate: ({ editor }) => onChange && onChange(editor.getHTML()),
  });

  const fileRef = useRef(null);
  const [showColor, setShowColor] = useState(false);
  const [showHl, setShowHl] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [showLink, setShowLink] = useState(false);
  const [linkMode, setLinkMode] = useState("note"); // "note" | "doc"
  const [linkNoteNum, setLinkNoteNum] = useState(1);
  const [linkDocId, setLinkDocId] = useState("");
  const [linkDocPage, setLinkDocPage] = useState(1);
  const [linkDraftId, setLinkDraftId] = useState("");

  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) editor.commands.setContent(value || "", false);
    // eslint-disable-next-line
  }, [editor]);

  useEffect(() => {
    if (editor) editor.setEditable(editable);
  }, [editable, editor]);

  if (!editor) return null;

  const btn = "px-2 py-1 border border-[#DCD9D0] rounded-sm text-[13px] hover:bg-[#FBFAF3] transition-colors duration-200 flex items-center gap-1";
  const active = "bg-[#1D3557] text-white border-[#1D3557] hover:bg-[#1D3557]";

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: tableRows, cols: tableCols, withHeaderRow: true }).run();
    setShowTable(false);
  };

  const onPickImage = () => fileRef.current?.click();
  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    if (file.size > 2 * 1024 * 1024) { alert("Please choose an image smaller than 2 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      editor.chain().focus().setImage({ src: reader.result, alt: file.name }).run();
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const openLinkDialog = () => {
    // Require some text selection so we know what to link
    const { from, to } = editor.state.selection;
    if (from === to) {
      alert("Select the word or sentence you want to hyperlink, then click the Link button.");
      return;
    }
    const attrs = editor.getAttributes("link") || {};
    if (attrs["data-eo-note"]) {
      setLinkMode("note");
      setLinkNoteNum(Number(attrs["data-eo-note"]) || 1);
    } else if (attrs["data-eo-doc"]) {
      setLinkMode("doc");
      setLinkDocId(String(attrs["data-eo-doc"]));
      setLinkDocPage(Number(attrs["data-eo-page"]) || 1);
    } else {
      setLinkMode(noteCount > 0 ? "note" : (attachedDocs.length > 0 ? "doc" : "note"));
      if (attachedDocs[0]) setLinkDocId(attachedDocs[0].id);
    }
    setShowLink(true);
  };

  const applyLink = () => {
    if (linkMode === "note") {
      if (noteCount < 1) { alert("This document has no finalized notes yet to link to."); return; }
      const n = Math.max(1, Math.min(noteCount, Number(linkNoteNum) || 1));
      editor.chain().focus().extendMarkRange("link").setLink({
        href: "#",
        "data-eo-note": String(n),
        "data-eo-doc": null,
        "data-eo-page": null,
        "data-eo-draft": null,
      }).run();
    } else if (linkMode === "draft") {
      if (!linkDraftId) { alert("Please select an in-file draft."); return; }
      editor.chain().focus().extendMarkRange("link").setLink({
        href: "#",
        "data-eo-draft": String(linkDraftId),
        "data-eo-note": null,
        "data-eo-doc": null,
        "data-eo-page": null,
      }).run();
    } else {
      if (!linkDocId) { alert("Please select an attached document."); return; }
      const p = Math.max(1, Number(linkDocPage) || 1);
      editor.chain().focus().extendMarkRange("link").setLink({
        href: "#",
        "data-eo-doc": String(linkDocId),
        "data-eo-page": String(p),
        "data-eo-note": null,
        "data-eo-draft": null,
      }).run();
    }
    setShowLink(false);
  };

  const removeLink = () => {
    editor.chain().focus().unsetLink().run();
    setShowLink(false);
  };

  const inTable = editor.isActive("table");
  const noteOptions = Array.from({ length: noteCount }, (_, i) => i + 1);
  const activeDoc = attachedDocs.find((d) => d.id === linkDocId);
  const canPageAnchor = activeDoc && activeDoc.extension?.toLowerCase() === "pdf";

  return (
    <div className="eo-editor">
      {editable && placeToolbar(
        <div data-testid="editor-toolbar" className="flex flex-wrap items-center gap-1 border-b border-[#DCD9D0] bg-[#FBFAF3] px-3 py-2 eo-no-print">
          {/* Font family */}
          <select
            title="Font family"
            value={editor.getAttributes("textStyle").fontFamily || ""}
            onChange={(e) => e.target.value ? editor.chain().focus().setFontFamily(e.target.value).run() : editor.chain().focus().unsetFontFamily().run()}
            className="h-7 px-1.5 text-[11px] border border-[#DCD9D0] bg-white rounded-sm w-[78px]"
            data-testid="editor-font-family"
          >
            <option value="">Font</option>
            {FONTS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          {/* Font size */}
          <select
            title="Font size"
            value={editor.getAttributes("textStyle").fontSize || ""}
            onChange={(e) => e.target.value ? editor.chain().focus().setFontSize(e.target.value).run() : editor.chain().focus().unsetFontSize().run()}
            className="h-7 px-1.5 text-[11px] border border-[#DCD9D0] bg-white rounded-sm w-[54px]"
            data-testid="editor-font-size"
          >
            <option value="">Size</option>
            {SIZES.map((s) => <option key={s} value={s}>{s.replace("px", "")}</option>)}
          </select>
          <div className="w-px h-5 bg-[#DCD9D0] mx-1" />
          <button data-testid="editor-btn-bold" onClick={() => editor.chain().focus().toggleBold().run()} className={`${btn} ${editor.isActive("bold") ? active : ""}`}><Bold size={14} /></button>
          <button data-testid="editor-btn-italic" onClick={() => editor.chain().focus().toggleItalic().run()} className={`${btn} ${editor.isActive("italic") ? active : ""}`}><Italic size={14} /></button>
          <button data-testid="editor-btn-underline" onClick={() => editor.chain().focus().toggleUnderline().run()} className={`${btn} ${editor.isActive("underline") ? active : ""}`}><UnderlineIcon size={14} /></button>
          {/* Text color */}
          <div className="relative">
            <button data-testid="editor-btn-color" title="Text color" onClick={() => { setShowColor((v) => !v); setShowHl(false); setShowTable(false); }} className={btn}><Palette size={14} /></button>
            {showColor && (
              <div className="absolute z-30 top-full mt-1 left-0 bg-white border border-[#DCD9D0] p-2 shadow-md flex flex-wrap gap-1 w-[160px]">
                {COLORS.map((c) => (
                  <button key={c} title={c} onClick={() => { editor.chain().focus().setColor(c).run(); setShowColor(false); }} className="w-5 h-5 border border-[#DCD9D0]" style={{ background: c }} data-testid={`editor-color-${c.replace("#","")}`} />
                ))}
                <button title="Remove color" onClick={() => { editor.chain().focus().unsetColor().run(); setShowColor(false); }} className="text-[11px] px-1.5 border border-[#DCD9D0] text-[#5C5C54]">Clear</button>
              </div>
            )}
          </div>
          {/* Highlight */}
          <div className="relative">
            <button data-testid="editor-btn-highlight" title="Highlight" onClick={() => { setShowHl((v) => !v); setShowColor(false); setShowTable(false); }} className={`${btn} ${editor.isActive("highlight") ? active : ""}`}><Highlighter size={14} /></button>
            {showHl && (
              <div className="absolute z-30 top-full mt-1 left-0 bg-white border border-[#DCD9D0] p-2 shadow-md flex flex-wrap gap-1 w-[160px]">
                {HIGHLIGHTS.filter(Boolean).map((c) => (
                  <button key={c} title={c} onClick={() => { editor.chain().focus().setHighlight({ color: c }).run(); setShowHl(false); }} className="w-5 h-5 border border-[#DCD9D0]" style={{ background: c }} data-testid={`editor-hl-${c.replace("#","")}`} />
                ))}
                <button title="Remove highlight" onClick={() => { editor.chain().focus().unsetHighlight().run(); setShowHl(false); }} className="text-[11px] px-1.5 border border-[#DCD9D0] text-[#5C5C54]">Clear</button>
              </div>
            )}
          </div>
          {/* Link */}
          <div className="relative">
            <button
              data-testid="editor-btn-link"
              title="Insert link (to a note or to an attached document page)"
              onClick={openLinkDialog}
              className={`${btn} ${editor.isActive("link") ? active : ""}`}
            >
              <LinkIcon size={14} />
            </button>
            {showLink && (
              <div className="absolute z-40 top-full mt-1 left-0 bg-white border border-[#DCD9D0] shadow-lg w-[320px] p-3" data-testid="editor-link-popover">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80]">// Insert link</div>
                  <button onClick={() => setShowLink(false)} className="text-[#8A8A80] hover:text-[#0A0A0A]"><XIcon size={14} /></button>
                </div>
                <div className="flex border border-[#DCD9D0] mb-3">
                  <button
                    data-testid="link-tab-note"
                    onClick={() => setLinkMode("note")}
                    className={`flex-1 py-1.5 text-[12px] ${linkMode === "note" ? "bg-[#1D3557] text-white" : "bg-white text-[#5C5C54]"}`}
                  >Note in this file</button>
                  <button
                    data-testid="link-tab-doc"
                    onClick={() => setLinkMode("doc")}
                    className={`flex-1 py-1.5 text-[12px] border-l border-[#DCD9D0] ${linkMode === "doc" ? "bg-[#1D3557] text-white" : "bg-white text-[#5C5C54]"}`}
                  >Attached doc page</button>
                  <button
                    data-testid="link-tab-draft"
                    onClick={() => setLinkMode("draft")}
                    className={`flex-1 py-1.5 text-[12px] border-l border-[#DCD9D0] ${linkMode === "draft" ? "bg-[#1D3557] text-white" : "bg-white text-[#5C5C54]"}`}
                  >In-file draft</button>
                </div>
                {linkMode === "draft" ? (
                  fileDrafts.length === 0 ? (
                    <div className="text-[12px] text-[#8A8A80] py-3">No in-file drafts yet. Open the split view and click <b>+ New draft</b> to create one, then come back here to link to it.</div>
                  ) : (
                    <>
                      <label className="block text-[11px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1">Draft to link</label>
                      <select
                        data-testid="link-draft-select"
                        value={linkDraftId}
                        onChange={(e) => setLinkDraftId(e.target.value)}
                        className="eo-input py-1.5 text-[13px]"
                      >
                        <option value="">Select a draft</option>
                        {fileDrafts.map((d) => (
                          <option key={d.id} value={d.id}>{d.title || "Untitled draft"} — by {d.author_name}</option>
                        ))}
                      </select>
                      <p className="text-[10px] text-[#8A8A80] mt-1.5 font-mono-eo">Clicking this link opens the draft in split view.</p>
                    </>
                  )
                ) : linkMode === "note" ? (
                  noteCount === 0 ? (
                    <div className="text-[12px] text-[#8A8A80] py-3">No finalized notes yet in this file. Once earlier authors close their sections, you'll be able to link back to them here.</div>
                  ) : (
                    <>
                      <label className="block text-[11px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1">Link to</label>
                      <select
                        data-testid="link-note-select"
                        value={linkNoteNum}
                        onChange={(e) => setLinkNoteNum(Number(e.target.value))}
                        className="eo-input py-1.5 text-[13px]"
                      >
                        {noteOptions.map((n) => (
                          <option key={n} value={n}>Note # {n}</option>
                        ))}
                      </select>
                    </>
                  )
                ) : (
                  attachedDocs.length === 0 ? (
                    <div className="text-[12px] text-[#8A8A80] py-3">No documents are attached to this file yet. Use <b>Attach</b> from the toolbar to attach a PDF or Word doc first, then come back here to link to a specific page.</div>
                  ) : (
                    <>
                      <label className="block text-[11px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1">Attached document</label>
                      <select
                        data-testid="link-doc-select"
                        value={linkDocId}
                        onChange={(e) => setLinkDocId(e.target.value)}
                        className="eo-input py-1.5 text-[13px] mb-2"
                      >
                        <option value="">Select a document</option>
                        {attachedDocs.map((d) => (
                          <option key={d.id} value={d.id}>{d.title} ({d.extension?.toUpperCase()})</option>
                        ))}
                      </select>
                      <label className="block text-[11px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1">Page</label>
                      <input
                        data-testid="link-doc-page"
                        type="number"
                        min="1"
                        value={linkDocPage}
                        onChange={(e) => setLinkDocPage(Math.max(1, parseInt(e.target.value) || 1))}
                        className="eo-input py-1.5 text-[13px]"
                      />
                      {!canPageAnchor && linkDocId && (
                        <p className="text-[10px] text-[#8A8A80] mt-1.5 font-mono-eo">Note: page-anchor jumps work for PDFs. Word docs will open at the first page.</p>
                      )}
                    </>
                  )
                )}
                <div className="flex justify-between items-center gap-2 mt-3 pt-3 border-t border-[#DCD9D0]">
                  {editor.isActive("link") ? (
                    <button data-testid="link-remove" onClick={removeLink} className="eo-btn-ghost text-[12px] border-[#E63946] text-[#E63946]">Remove link</button>
                  ) : <span />}
                  <div className="flex gap-2">
                    <button onClick={() => setShowLink(false)} className="eo-btn-ghost text-[12px]">Cancel</button>
                    <button data-testid="link-apply" onClick={applyLink} className="eo-btn-primary text-[12px]">Apply</button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="w-px h-5 bg-[#DCD9D0] mx-1" />
          <button data-testid="editor-btn-h1" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={`${btn} ${editor.isActive("heading", { level: 1 }) ? active : ""}`}><Heading1 size={14} /></button>
          <button data-testid="editor-btn-h2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={`${btn} ${editor.isActive("heading", { level: 2 }) ? active : ""}`}><Heading2 size={14} /></button>
          <button data-testid="editor-btn-ul" onClick={() => editor.chain().focus().toggleBulletList().run()} className={`${btn} ${editor.isActive("bulletList") ? active : ""}`}><List size={14} /></button>
          <button data-testid="editor-btn-ol" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={`${btn} ${editor.isActive("orderedList") ? active : ""}`}><ListOrdered size={14} /></button>
          <button data-testid="editor-btn-quote" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={`${btn} ${editor.isActive("blockquote") ? active : ""}`}><Quote size={14} /></button>
          <div className="w-px h-5 bg-[#DCD9D0] mx-1" />
          <button title="Align left" data-testid="editor-align-left" onClick={() => editor.chain().focus().setTextAlign("left").run()} className={`${btn} ${editor.isActive({ textAlign: "left" }) ? active : ""}`}><AlignLeft size={14} /></button>
          <button title="Align center" data-testid="editor-align-center" onClick={() => editor.chain().focus().setTextAlign("center").run()} className={`${btn} ${editor.isActive({ textAlign: "center" }) ? active : ""}`}><AlignCenter size={14} /></button>
          <button title="Align right" data-testid="editor-align-right" onClick={() => editor.chain().focus().setTextAlign("right").run()} className={`${btn} ${editor.isActive({ textAlign: "right" }) ? active : ""}`}><AlignRight size={14} /></button>
          <button title="Justify" data-testid="editor-align-justify" onClick={() => editor.chain().focus().setTextAlign("justify").run()} className={`${btn} ${editor.isActive({ textAlign: "justify" }) ? active : ""}`}><AlignJustify size={14} /></button>
          <div className="w-px h-5 bg-[#DCD9D0] mx-1" />
          {/* Table */}
          <div className="relative">
            <button data-testid="editor-btn-table" title="Insert table" onClick={() => { setShowTable((v) => !v); setShowColor(false); setShowHl(false); }} className={`${btn} ${inTable ? active : ""}`}><TableIcon size={14} /></button>
            {showTable && (
              <div className="absolute z-30 top-full mt-1 left-0 bg-white border border-[#DCD9D0] p-3 shadow-md w-[220px]">
                <div className="text-[11px] font-mono-eo uppercase tracking-widest text-[#8A8A80] mb-2">// Insert table</div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-[12px]">Rows</label>
                  <input type="number" min="1" max="20" value={tableRows} onChange={(e) => setTableRows(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))} className="eo-input py-1 px-2 text-[12px] w-[60px]" data-testid="editor-table-rows" />
                  <label className="text-[12px]">Cols</label>
                  <input type="number" min="1" max="10" value={tableCols} onChange={(e) => setTableCols(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))} className="eo-input py-1 px-2 text-[12px] w-[60px]" data-testid="editor-table-cols" />
                </div>
                <button onClick={insertTable} className="eo-btn-primary w-full text-[12px]" data-testid="editor-table-insert">Insert {tableRows} × {tableCols}</button>
              </div>
            )}
          </div>
          {inTable && (
            <>
              <button title="Add row" onClick={() => editor.chain().focus().addRowAfter().run()} className={btn}><Plus size={14} /><span className="text-[10px]">row</span></button>
              <button title="Add column" onClick={() => editor.chain().focus().addColumnAfter().run()} className={btn}><Plus size={14} /><span className="text-[10px]">col</span></button>
              <button title="Delete row" onClick={() => editor.chain().focus().deleteRow().run()} className={btn}><Minus size={14} /><span className="text-[10px]">row</span></button>
              <button title="Delete column" onClick={() => editor.chain().focus().deleteColumn().run()} className={btn}><Minus size={14} /><span className="text-[10px]">col</span></button>
              <button title="Delete table" onClick={() => editor.chain().focus().deleteTable().run()} className={`${btn} border-[#E63946] text-[#E63946]`}><Trash2 size={13} /></button>
            </>
          )}
          {/* Image */}
          <button data-testid="editor-btn-image" title="Insert image" onClick={onPickImage} className={btn}><ImageIcon size={14} /></button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFileChange} className="hidden" data-testid="editor-image-input" />
          <div className="w-px h-5 bg-[#DCD9D0] mx-1" />
          <button onClick={() => editor.chain().focus().undo().run()} className={btn}><Undo2 size={14} /></button>
          <button onClick={() => editor.chain().focus().redo().run()} className={btn}><Redo2 size={14} /></button>
        </div>
      )}
      <EditorContent editor={editor} data-testid="editor-content" />
    </div>
  );
}
